import type { DailyScore, MatchOutcome, TeachStep } from "@obean/game-contract";
import {
  dailyNumber,
  dailyOf,
  dailyScoreText,
  dailyTier,
  dayOf,
  mergeProgress,
  outcomeProps,
  playProps,
} from "@obean/game-contract";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import games from "virtual:obean-games";
import { GameHost } from "../host/host.js";
import { LocalDriver } from "../host/local-driver.js";
import { GameUnavailable, loadGame, reloadOnce } from "../host/loader.js";
import type { GameRef } from "../host/loader.js";
import { track } from "../host/telemetry.js";
import { PlayerInput, TouchControls, keyboardHint } from "../input/index.js";
import { TeachCard, teachingNow, useTeach } from "../teach/TeachCard.js";
import { rememberLastPlay } from "../home/home.js";
import { api } from "../net/api.js";
import { MissionMap } from "../map/MissionMap.js";
import { HourNote, openSettings } from "../settings/SettingsSheet.js";
import { StarsMoment } from "../moments/moments.js";
import { usePlaying } from "../net/presence.js";
import { cardLines, dailyShareLine, headlineOf, tierMarks } from "../share/result-card.js";
import { AdBanner, useBannerInPlay } from "../ads/AdBanner.js";
import { FullAd } from "../ads/FullAd.js";
import { adsForMe, countSoloResult, fullAdDue, fullAdShown, loadAds } from "../ads/ads.js";
import { GameCards } from "./GameCards.js";
import type { SoloMission, SoloProgress } from "../solo/progress.js";
import {
  loadProgress,
  recordResult,
  saveProgress,
  syncProgress,
  soloMissions,
} from "../solo/progress.js";
import { soloTrace } from "../host/trace-plan.js";
import type { DailyBest } from "../solo/daily.js";
import {
  dailyBest,
  dailyProps,
  dailyStreak,
  dayName,
  pastDailies,
  playedDays,
  playedTodayText,
  recordDaily,
  shareDaily,
  streakText,
} from "../solo/daily.js";
import { EveryoneDaily } from "../solo/EveryoneDaily.js";
import { FriendsDaily } from "../solo/FriendsDaily.js";
import type { Earned } from "../loop/beans.js";
import { award, beansOf, levelOf, loadWallet, wonBy } from "../loop/beans.js";
import type { Feat } from "../loop/feats.js";
import { FeatsEarned } from "../loop/FeatsEarned.js";
import { keepFeats, keepWallet } from "../loop/profile.js";
import { BeansEarned } from "../loop/BeansEarned.js";
import type { Step } from "../solo/next-step.js";
import { lineProgress, nextSteps, otherGameFor, tipFor, unlockLine } from "../solo/next-step.js";
import { diagMark } from "../host/diagnostics.js";
import { TellUs } from "./TellUs.js";

/** Test handle (the e2e tests read and nudge the simulation through it). */
type SoloWindow = Window & { obeanSolo?: { state: () => unknown } };

const WHY: Record<SoloMission["why"], string> = {
  "": "",
  win_previous: "Win the mission before",
  win_after: "Opens after a win",
  pack: "With Obean Pass",
};

/**
 * A daily (P4-TANK-03): its day and the seed everyone plays it with. `past`: one from the archive
 * (ADR 0041), played again: it shows its tier but counts for nothing of today's.
 */
type Daily = { day: string; seed: number; past?: boolean };

type Phase =
  /** `fresh`: missions the last play opened, for the map's moment (P5-UX-03). */
  | { kind: "pick"; fresh?: readonly string[] }
  /** The full-screen ad before a try (ADR 0026: three losses in a row, without the Pass). */
  | { kind: "ad"; missionId: string; run: number; daily?: Daily }
  | { kind: "play"; missionId: string; run: number; daily?: Daily }
  | {
      kind: "results";
      missionId: string;
      outcome: MatchOutcome;
      run: number;
      /** The missions open before this play (P5-LOOP-01: what a first win opened). */
      wasOpen: string[];
      daily?: Daily & { best: DailyBest; isNew: boolean };
      /** A past daily from the archive (ADR 0041). */
      archive?: Daily;
      /** What the play earned, and the beans in all after it (ADR 0043). */
      beans?: { earned: Earned; total: number };
      /** The achievements it earned just now (P5-LOOP-03). */
      feats?: Feat[];
    };

const DAILY_RESULT: Record<DailyBest["result"], string> = { win: "won", loss: "lost" };
/** A daily best in a few words: won with stars, or how far it got. */
const bestText = (b: DailyBest, score?: DailyScore) => {
  const tier = b.tier === undefined ? undefined : score?.tiers[b.tier];
  if (tier && b.told) return `${tier}, ${b.told}`;
  return `${DAILY_RESULT[b.result]}${b.stars > 0 ? `, ${"★".repeat(b.stars)}` : ""}${b.score > 0 ? `, ${String(b.score)} points` : ""}`;
};

/**
 * The archive (ADR 0041): the past month's dailies, each playable again with the same mission and
 * seed everyone had that day, and your best of that day if you played it.
 */
function PastDailies(props: {
  gameId: string;
  past: readonly { day: string; missionId: string; seed: number }[];
  nameOf: (missionId: string) => string;
  score: DailyScore | undefined;
  onPlay: (d: { day: string; missionId: string; seed: number }) => void;
}) {
  if (props.past.length === 0) return null;
  return (
    <details class="past-dailies">
      <summary>Past dailies</summary>
      <ul>
        {props.past.map((d) => {
          const best = dailyBest(props.gameId, d.day);
          return (
            <li key={d.day}>
              <button
                type="button"
                class="secondary"
                onClick={() => {
                  props.onPlay(d);
                }}
              >
                #{dailyNumber(d.day)} · {dayName(d.day)} · {props.nameOf(d.missionId)}
              </button>
              {best && <span class="dim"> Your best that day: {bestText(best, props.score)}.</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/** The daily streak, from two days in a row (ADR 0041): a nudge, no rewards or reminders. */
function Streak({ day }: { day: string }) {
  const text = streakText(dailyStreak(playedDays(), day));
  return text ? <p class="hint streak">Daily streak: {text}, in any game.</p> : null;
}

/** "1,240 people have played today's daily" (ADR 0043 part 5): a count, never who. Quiet on error. */
function PlayedToday({ gameId, day }: { gameId: string; day: string }) {
  const [played, setPlayed] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    api.dailyEveryone(gameId, day).then(
      (e) => {
        if (live) setPlayed(e.played);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [gameId, day]);
  return played ? <p class="hint played-today">{playedTodayText(played)}</p> : null;
}

/** This play's tier at the daily, in the game's marks, its name and the score (ADR 0041). */
function DailyTier({ score, outcome }: { score: DailyScore; outcome: MatchOutcome }) {
  const tier = dailyTier(score, outcome);
  return (
    <p class="daily-tier">
      <span aria-hidden="true">{tierMarks(score.mark, tier)}</span>{" "}
      <strong>{score.tiers[tier]}</strong>
      <span class="visually-hidden"> (tier {tier + 1} of 5)</span> ·{" "}
      {dailyScoreText(score, outcome.stats)}
    </p>
  );
}

/**
 * The daily's result as a line to paste anywhere (ADR 0041): the share sheet where there is one,
 * else copied, counted as result_card_shared either way.
 */
function ShareDaily({ text, props }: { text: string; props: Record<string, string> }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ text });
        track("result_card_shared", { ...props, daily: true, method: "text_share" });
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopied(true);
      track("result_card_shared", { ...props, daily: true, method: "copy" });
    } catch {
      // The share sheet was closed, or the clipboard refused: nothing shared.
    }
  };
  return (
    <div class="share-daily">
      <pre>{text}</pre>
      <button
        type="button"
        class="secondary"
        onClick={() => {
          void share();
        }}
      >
        {copied ? "Copied" : "Share"}
      </button>
    </div>
  );
}

/** A step's button: what it does, in a few words. */
function stepLabel(step: Step, result: "win" | "loss"): string {
  if (step.kind === "next") return `Next: ${step.name}`;
  if (step.kind === "daily") return "Today's daily";
  if (step.kind === "game") return `Try ${step.name}`;
  return result === "loss" ? "Try again" : "Play again";
}

/** The game named in the address (`/solo?game=<id>`), or null for the chooser. */
const gameInUrl = () => new URLSearchParams(location.search).get("game");

/**
 * Solo mode (ADR 0020): `/solo` lets you choose a game by its picture (founder, 2026-10-15: one
 * way in, every game in it); `/solo?game=<id>` goes straight to that game's missions.
 */
export function Solo() {
  const [gameId, setGameId] = useState<string | null>(gameInUrl);
  // The browser's back button returns to the chooser.
  useEffect(() => {
    const onPop = () => {
      setGameId(gameInUrl());
    };
    addEventListener("popstate", onPop);
    return () => {
      removeEventListener("popstate", onPop);
    };
  }, []);
  if (gameId === null)
    return (
      <section class="landing solo-choose">
        <SoloBanner />
        <h1>Play solo</h1>
        <p>
          Pick a game. You play with bots, right here in your browser; your progress stays on this
          device.
        </p>
        <GameCards
          onPick={(id) => {
            history.pushState(null, "", `/solo?game=${encodeURIComponent(id)}`);
            setGameId(id);
          }}
        />
        <p class="solo-link">
          Rather play with friends? <a href="/new">Make a room</a>.
        </p>
      </section>
    );
  return <SoloGame key={gameId} gameId={gameId} />;
}

/**
 * One game alone: no server, no room. Pick a mission, a role and how many bots, play, and see the
 * result, with a way to play it with a friend instead. Progress stays in this browser
 * (solo/progress.ts).
 */
function SoloGame({ gameId }: { gameId: string }) {
  const game = games.find((g) => g.id === gameId);
  const [phase, setPhase] = useState<Phase>({ kind: "pick" });
  const [progress, setProgress] = useState<SoloProgress>(() => loadProgress(gameId));
  // Friends see which game this is (ADR 0031).
  usePlaying(gameId);
  /** Signed in: the person whose progress this is (ADR 0029); null for the browser's guest. */
  const [profile, setProfile] = useState<string | null>(null);
  /** New bests sent to friends this visit: each one reads their list again. */
  const [shared, setShared] = useState(0);
  const [packs, setPacks] = useState<readonly string[]>([]);
  const [role, setRole] = useState(game?.loadouts[0]?.id ?? "");
  const [bots, setBots] = useState(game?.soloMinBots ?? 1);
  /** Battles for four: two against two (ADR 0028). */
  const [teams, setTeams] = useState(false);
  const canTeams = game?.missions.some((m) => m.teams) === true && bots === 3;

  // Ads without the Pass (ADR 0026): /api/me says whether this person sees them.
  useEffect(loadAds, []);
  const ads = adsForMe.value === true;
  /** Play a mission: after three losses in a row on it, the full-screen ad comes first. */
  const go = (missionId: string, run: number, daily?: Daily) => {
    const d = daily ? { daily } : {};
    setPhase(
      ads && fullAdDue(gameId, missionId)
        ? { kind: "ad", missionId, run, ...d }
        : { kind: "play", missionId, run, ...d },
    );
  };
  // Today's daily (P4-TANK-03): the same mission and seed for everyone, open to all.
  const today = useMemo(() => (game ? dailyOf(game, dayOf(Date.now())) : null), [gameId]);

  // The Pass's missions open here too for Pass members, or for everyone while it isn't sold (ADR 0022).
  useEffect(() => {
    api.me().then(
      (me) => {
        setPacks(me.packs);
        // Signed in, progress is the person's, synced with every device they play on (ADR 0029).
        const person = me.signedIn === true ? (me.personId ?? null) : null;
        if (person !== null)
          void syncProgress(gameId, person, me.ownBrowser === true, api).then((synced) => {
            setProfile(person);
            setProgress(synced);
          });
      },
      () => {
        // No guest yet, or offline: the free missions.
      },
    );
  }, []);

  // Started from the start page (P5-UX-04): `&mission=<id>` plays that mission if it's open, and
  // `&daily=1` today's daily; once, then the address is the game's again.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !game) return;
    started.current = true;
    const q = new URLSearchParams(location.search);
    const mission = q.get("mission");
    const wantsDaily = q.get("daily") === "1";
    // `&daily=<day>`: a past daily from the archive (ADR 0041).
    const past = pastDailies(game, dayOf(Date.now())).find((d) => d.day === q.get("daily"));
    if (mission === null && !wantsDaily && !past) return;
    history.replaceState(history.state, "", `/solo?game=${encodeURIComponent(gameId)}`);
    if (past) go(past.missionId, 0, { day: past.day, seed: past.seed, past: true });
    else if (wantsDaily && today) go(today.missionId, 0, { day: today.day, seed: today.seed });
    else if (soloMissions(game.missions, progress, packs).some((m) => m.id === mission && m.open))
      go(mission ?? "", 0);
  }, []);

  if (!game)
    return (
      <section class="landing">
        <h1>Play solo</h1>
        <p role="alert">There's no game called “{gameId}” here.</p>
        <a href="/solo">Choose a game</a>
      </section>
    );

  const list = soloMissions(game.missions, progress, packs);
  const mapList = list.map((m) => ({ ...m, why: WHY[m.why] }));
  const minBots = game.soloMinBots;
  const maxBots = Math.max(minBots, game.players.max - 1);

  if (phase.kind === "ad")
    return (
      <FullAd
        gameId={gameId}
        onDone={() => {
          fullAdShown(gameId, phase.missionId);
          setPhase({
            kind: "play",
            missionId: phase.missionId,
            run: phase.run,
            ...(phase.daily ? { daily: phase.daily } : {}),
          });
        }}
      />
    );

  if (phase.kind === "play")
    return (
      <SoloMatch
        ads={ads}
        key={phase.run}
        gameId={gameId}
        version={game.version}
        missionId={phase.missionId}
        {...(phase.daily ? { daily: phase.daily } : {})}
        role={role}
        teams={
          canTeams && teams && game.missions.find((m) => m.id === phase.missionId)?.teams === true
        }
        bots={bots}
        buttons={game.buttons}
        touch={game.touch}
        teach={game.teach}
        onEnd={(outcome) => {
          countSoloResult(gameId, phase.missionId, outcome.result);
          const next = recordResult(progress, phase.missionId, outcome);
          saveProgress(gameId, next, undefined, profile);
          setProgress(next);
          // Signed in: the win goes to the person's copy too, and anything newer comes back.
          if (profile !== null)
            api.saveProgress(gameId, next).then(
              ({ progress: merged }) => {
                saveProgress(gameId, merged, undefined, profile);
                setProgress((current) => mergeProgress(current, merged));
              },
              () => {
                // Offline: the next sync sends it.
              },
            );
          const daily =
            phase.daily && !phase.daily.past
              ? {
                  ...phase.daily,
                  ...recordDaily(gameId, phase.daily.day, outcome, localStorage, game.dailyScore),
                }
              : undefined;
          // Signed in, a new best goes to friends too (privacy policy 1.8); then their list again.
          if (daily?.isNew && profile !== null)
            void shareDaily(gameId, daily.day, daily.best, api).then((sent) => {
              if (sent) setShared((n) => n + 1);
            });
          // Beans (ADR 0043): this player is slot 0, and side 0 two against two.
          const beans = award(
            loadWallet(profile),
            {
              gameId,
              decided: true,
              won: wonBy(outcome, 0, 0),
              stats: outcome.stats,
              daily: daily !== undefined,
              together: false,
            },
            game.beans,
            dayOf(Date.now()),
          );
          const run = phase.run;
          // Achievements (ADR 0043, P5-LOOP-03): this play counted, and anything it earned shown.
          void keepFeats(
            {
              gameId,
              missionId: phase.missionId,
              won: wonBy(outcome, 0, 0),
              stats: outcome.stats,
              together: false,
            },
            game.achievements,
            {
              level: levelOf(beansOf(beans.wallet)).level,
              games: games.filter((g) => g.stage === "live").map((g) => g.id),
            },
            profile,
            dayOf(Date.now()),
          ).then((earned) => {
            if (earned.length > 0)
              setPhase((now) =>
                now.kind === "results" && now.run === run ? { ...now, feats: earned } : now,
              );
          });
          // Signed in, the person's other devices' beans come back with the sync (#585).
          void keepWallet(beans.wallet, profile).then((kept) => {
            setPhase((now) =>
              now.kind === "results" && now.run === run && now.beans
                ? { ...now, beans: { ...now.beans, total: beansOf(kept) } }
                : now,
            );
          });
          setPhase({
            kind: "results",
            missionId: phase.missionId,
            outcome,
            run: phase.run,
            // What was open before this play, so the result can say what a first win opened.
            wasOpen: list.filter((m) => m.open).map((m) => m.id),
            ...(daily ? { daily } : {}),
            ...(phase.daily?.past ? { archive: phase.daily } : {}),
            beans: { earned: beans.earned, total: beansOf(beans.wallet) },
          });
        }}
      />
    );

  if (phase.kind === "results") {
    const mission = list.find((m) => m.id === phase.missionId);
    const lines = cardLines({
      result: phase.outcome.result === "win" ? "win" : "loss",
      stats: phase.outcome.stats,
      mission: mission?.name ?? phase.missionId,
      tickHz: game.tickHz,
    });
    // A duel against bots names the winner: slot 0 is this player, team 0 is theirs.
    const headline = headlineOf(
      { result: phase.outcome.result === "win" ? "win" : "loss", stats: phase.outcome.stats },
      { slot: 0, team: 0 },
      () => "A bot",
    );
    const result = phase.outcome.result === "win" ? "win" : "loss";
    // The next step (ADR 0043): the main one first; each tap is counted.
    const steps = nextSteps({
      result,
      missionId: phase.missionId,
      list,
      daily: today
        ? { missionId: today.missionId, played: dailyBest(gameId, today.day) !== null }
        : null,
      wasDaily: phase.daily !== undefined,
      otherGame: otherGameFor(
        gameId,
        games.map((g) => ({ id: g.id, name: g.displayName })),
        (id) => Object.keys(loadProgress(id).won).length,
      ),
    });
    const line = lineProgress(list, phase.missionId);
    const unlock = unlockLine(
      game.missions,
      phase.missionId,
      result,
      (id) => list.some((m) => m.id === id && m.open),
      (id) => phase.wasOpen.includes(id),
    );
    const tip = result === "loss" ? tipFor(game.tips, phase.run) : null;
    const play = (missionId: string) => {
      // Again on the daily (or a past one) is the same daily again; another mission isn't.
      const d = phase.daily ?? phase.archive;
      go(
        missionId,
        phase.run + 1,
        d && missionId === phase.missionId
          ? { day: d.day, seed: d.seed, ...(d.past ? { past: true } : {}) }
          : undefined,
      );
    };
    const takeStep = (step: Step) => {
      if (step.kind === "game") location.assign(`/solo?game=${encodeURIComponent(step.gameId)}`);
      else if (step.kind === "daily" && today)
        go(today.missionId, phase.run + 1, { day: today.day, seed: today.seed });
      else play(step.missionId);
    };
    return (
      <section class="landing solo">
        {ads && <AdBanner where="solo" gameId={gameId} />}
        <h1>{headline}</h1>
        <p class="solo-result">
          {phase.daily ? `Today's daily #${String(dailyNumber(phase.daily.day))}: ` : ""}
          {phase.archive
            ? `Daily #${String(dailyNumber(phase.archive.day))}, ${dayName(phase.archive.day)}: `
            : ""}
          {lines.mission} <StarsMoment n={phase.outcome.stats["stars"]} />
        </p>
        {(phase.daily ?? phase.archive) && game.dailyScore && (
          <DailyTier score={game.dailyScore} outcome={phase.outcome} />
        )}
        {phase.archive && (
          <p class="hint">
            A past daily: it shows your tier, but today's comparison and your friends see only
            today's.
          </p>
        )}
        {phase.daily && game.dailyScore && (
          <EveryoneDaily
            gameId={gameId}
            day={phase.daily.day}
            score={game.dailyScore}
            outcome={phase.outcome}
          />
        )}
        {phase.daily && (
          <p class="hint">
            {phase.daily.isNew ? "Your best today! " : ""}Your best at today's daily:{" "}
            {bestText(phase.daily.best, game.dailyScore)}. Everyone gets the same one today; a new
            one tomorrow.
          </p>
        )}
        {phase.daily && <Streak day={phase.daily.day} />}
        {phase.daily && profile !== null && (
          <FriendsDaily gameId={gameId} day={phase.daily.day} version={shared} />
        )}
        {phase.daily && (
          <ShareDaily
            text={dailyShareLine(
              {
                result,
                stats: phase.outcome.stats,
                mission: lines.mission,
                tickHz: game.tickHz,
                game: game.displayName,
                gameId,
                dailyNumber: dailyNumber(phase.daily.day),
                score: game.dailyScore,
              },
              location.origin,
            )}
            props={{ result, mission_id: phase.missionId, game_id: gameId }}
          />
        )}
        {lines.facts.length > 0 && <p class="hint">{lines.facts.join(" · ")}</p>}
        {phase.beans && <BeansEarned earned={phase.beans.earned} total={phase.beans.total} />}
        {phase.feats && <FeatsEarned earned={phase.feats} />}
        <HourNote />
        {tip && (
          <p class="tip">
            <strong>Tip:</strong> {tip}
          </p>
        )}
        {(line || unlock) && (
          <div class="line-progress">
            {line && (
              <>
                <span>
                  {line.name}: {line.won} of {line.total} won
                </span>
                <progress max={line.total} value={line.won} aria-hidden="true" />
              </>
            )}
            {unlock && <span class="unlock">{unlock}</span>}
          </div>
        )}
        <div class="actions">
          {steps.map((step, i) => (
            <button
              key={`${step.kind}-${i}`}
              type="button"
              class={i === 0 ? "" : "secondary"}
              onClick={() => {
                track("next_step", {
                  step: step.kind,
                  main: i === 0,
                  game_id: gameId,
                  mission_id: phase.missionId,
                  place: "solo",
                });
                takeStep(step);
              }}
            >
              {stepLabel(step, result)}
            </button>
          ))}
          <button
            type="button"
            class="secondary"
            onClick={() => {
              const fresh = soloMissions(game.missions, progress, packs)
                .filter((m) => m.open && !phase.wasOpen.includes(m.id))
                .map((m) => m.id);
              setPhase({ kind: "pick", fresh });
            }}
          >
            Choose a mission
          </button>
        </div>
        <div class="card solo-friend">
          <p>
            <strong>Better with a friend.</strong> Make a room, send the link, and play it together
            on any phone or laptop.
          </p>
          <a
            class="button"
            href="/new?from=solo"
            onClick={() => {
              track("solo_cta", { action: "invite", game_id: gameId, mission_id: phase.missionId });
            }}
          >
            Play with a friend
          </a>
        </div>
      </section>
    );
  }

  const start = (missionId: string) => {
    go(missionId, Date.now());
  };
  return (
    <section class="landing solo">
      {ads && <AdBanner where="solo" gameId={gameId} />}
      <h1>Play solo</h1>
      <p>
        {bots === 0
          ? "Just you"
          : minBots === 0
            ? `You and ${String(bots)} ${bots === 1 ? "bot" : "bots"}`
            : `You and ${bots === 1 ? "a bot teammate" : `${String(bots)} bot teammates`}`}
        , the same missions as with friends. Your progress stays in this browser.
      </p>
      {game.loadouts.length > 0 && (
        <fieldset class="card">
          <legend>Your role</legend>
          <div class="choices">
            {game.loadouts.map((l) => (
              <button
                type="button"
                key={l.id}
                aria-pressed={role === l.id}
                title={l.summary}
                onClick={() => {
                  setRole(l.id);
                }}
              >
                {l.name}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {maxBots > minBots && (
        <fieldset class="card">
          <legend>{minBots === 0 ? "Bots" : "Bot teammates"}</legend>
          <div class="choices">
            {Array.from({ length: maxBots - minBots + 1 }, (_, i) => i + minBots).map((n) => (
              <button
                type="button"
                key={n}
                aria-pressed={bots === n}
                onClick={() => {
                  setBots(n);
                }}
              >
                {n}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {canTeams && (
        <fieldset class="card">
          <legend>Battle</legend>
          <div class="choices">
            {[false, true].map((on) => (
              <button
                type="button"
                key={String(on)}
                aria-pressed={teams === on}
                onClick={() => {
                  setTeams(on);
                }}
              >
                {on ? "2 v 2: you and a bot against two" : "Everyone for themselves"}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      {today && (
        <div class="card daily">
          <h2>Today's daily #{dailyNumber(today.day)}</h2>
          <p>
            {game.missions.find((m) => m.id === today.missionId)?.name ?? today.missionId}: the same
            for everyone today, open to all.
            {(() => {
              const best = dailyBest(gameId, today.day);
              return best ? ` Your best today: ${bestText(best, game.dailyScore)}.` : "";
            })()}
          </p>
          <PlayedToday gameId={gameId} day={today.day} />
          <Streak day={today.day} />
          <button
            type="button"
            onClick={() => {
              go(today.missionId, Date.now(), { day: today.day, seed: today.seed });
            }}
          >
            Play today's daily
          </button>
          {profile !== null && <FriendsDaily gameId={gameId} day={today.day} version={shared} />}
          <PastDailies
            gameId={gameId}
            past={pastDailies(game, today.day)}
            nameOf={(id) => game.missions.find((m) => m.id === id)?.name ?? id}
            score={game.dailyScore}
            onPlay={(d) => {
              go(d.missionId, Date.now(), { day: d.day, seed: d.seed, past: true });
            }}
          />
        </div>
      )}
      <fieldset class="card missions">
        <legend>Mission</legend>
        <MissionMap
          missions={mapList}
          art={game.art}
          note={(id) =>
            id === "run" && progress.bestRun > 0
              ? `Best: ${String(progress.bestRun)} cleared`
              : undefined
          }
          fresh={phase.fresh}
          onPick={start}
        />
      </fieldset>
      <p class="hint">
        <a href="/solo">Another game</a> · Rather play with a friend?{" "}
        <a href={`/new?game=${gameId}`}>Make a room</a>. ·{" "}
        <button type="button" class="link" onClick={openSettings}>
          Settings
        </button>
      </p>
    </section>
  );
}

/** One solo match: the game in this browser, this device in slot 0, bots in the others. */
function SoloMatch(props: {
  /** A banner at the top without the Pass (ADR 0026). */
  ads: boolean;
  gameId: string;
  version: string;
  missionId: string;
  role: string;
  /** Two against two: you and a bot against two (ADR 0028). */
  teams: boolean;
  /** Today's daily: everyone's seed for the day (P4-TANK-03). */
  daily?: Daily;
  bots: number;
  buttons: readonly { button: "primary" | "secondary" | "ability"; label: string }[];
  /** How touch screens steer it (ADR 0023). */
  touch: "stick" | "swipe" | "tap";
  /** The game's first play (manifest.teach, P5-UX-05). */
  teach: readonly TeachStep[];
  onEnd: (outcome: MatchOutcome) => void;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("Loading…");
  const [loadFailed, setLoadFailed] = useState(false);
  const input = useMemo(() => new PlayerInput(), []);
  const touchUi = matchMedia("(pointer: coarse)").matches;
  const onEnd = useRef(props.onEnd);
  onEnd.current = props.onEnd;
  const banner = useBannerInPlay(props.ads);
  // The first play teaches (P5-UX-05).
  const teach = useTeach(props.gameId, props.teach);

  useEffect(() => {
    const ref: GameRef = { id: props.gameId, version: props.version };
    let host: GameHost | null = null;
    let cancelled = false;
    let ended: ReturnType<typeof setTimeout> | null = null;
    const isCancelled = () => cancelled;
    const startedAt = performance.now();

    const start = async () => {
      const loadStartedAt = performance.now();
      const game = await loadGame(ref);
      const container = stage.current;
      if (isCancelled() || !container) return;
      host = await GameHost.load({ container, game, ref, track, loadStartedAt });
      if (isCancelled()) {
        host.exit();
        return;
      }
      // Two against two (ADR 0028): you and the bot in slot 2 against slots 1 and 3, as the
      // room's sides go by seat order.
      const players = Array.from({ length: 1 + props.bots }, (_, slot) => ({
        ...(slot === 0 && props.role
          ? { slot, personId: "solo-0", loadout: props.role }
          : { slot, personId: `solo-${String(slot)}` }),
        ...(props.teams ? { team: slot % 2 } : {}),
      }));
      const local = host;
      let playFrom = performance.now();
      const botOf = players.map((p) => (p.slot === 0 ? null : (game.createBot?.(p.slot) ?? null)));
      const driver: LocalDriver<unknown, unknown> = new LocalDriver(
        game.rules,
        {
          seed: props.daily?.seed ?? Math.floor(Math.random() * 2 ** 31),
          missionId: props.missionId,
          players,
        },
        (slot) => {
          if (slot === 0) {
            const controls = input.sample();
            const mine = local.input(controls);
            const at = teach.see(controls, mine, performance.now());
            // How long a new player takes to do something (P5-UX-05), once a play.
            if (at !== null)
              track("first_move", {
                ...playProps(game.manifest, props.missionId, props.teams),
                place: "solo",
                ms: Math.round(at - playFrom),
                first_play: teach.teaching,
              });
            return mine;
          }
          const bot = botOf[slot];
          return bot ? bot(driver.state) : game.rules.idleInput();
        },
        () => performance.now(),
      );
      (window as SoloWindow).obeanSolo = { state: () => driver.state };
      // The same play properties as a room's match (P5-AN-04), and one id for the whole play.
      const play = playProps(game.manifest, props.missionId, props.teams);
      const matchId = `solo-${crypto.randomUUID()}`;
      // Traced (ADR 0035): solo rolls against the game's share for each play.
      const traceWhy = await soloTrace(props.gameId, props.version);
      if (isCancelled()) return;
      rememberLastPlay(props.gameId); // the start page's Carry on (P5-UX-04)
      track("solo_started", {
        ...play,
        match_id: matchId,
        bots: props.bots,
        role: props.role || null,
        ...dailyProps(props.daily),
      });
      playFrom = performance.now();
      host.start(
        {
          matchId,
          missionId: props.missionId,
          mySlot: 0,
          players,
          ...(traceWhy
            ? { trace: { id: `${matchId}-s0`, why: traceWhy, place: "solo" as const, slot: 0 } }
            : {}),
        },
        driver,
        (outcome) => {
          track("solo_ended", {
            ...play,
            match_id: matchId,
            bots: props.bots,
            ...dailyProps(props.daily),
            duration_s: Math.round((performance.now() - startedAt) / 1000),
            // The result, stars, missions and the game's declared stats (stat_<name>).
            ...outcomeProps(game.manifest, outcome),
          });
          // Let the game's own result banner show for a moment first.
          ended = setTimeout(() => {
            onEnd.current(outcome);
          }, 1800);
        },
      );
      setStatus("");
    };

    start().catch((err: unknown) => {
      if (err instanceof GameUnavailable && reloadOnce(err.ref)) return;
      setStatus(err instanceof Error ? err.message : String(err));
      setLoadFailed(true);
      diagMark("load_failed");
    });
    return () => {
      cancelled = true;
      if (ended) clearTimeout(ended);
      host?.exit();
      input.dispose();
    };
  }, [input]);

  return (
    <section
      class={`${
        // A tap game without buttons has no bar to leave room for (ADR 0027).
        touchUi && (props.touch !== "tap" || props.buttons.length > 0)
          ? `play ${props.touch}`
          : "play"
      }${banner ? " ad" : ""}${!status && teachingNow(teach) ? " teaching" : ""}`}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      {props.ads && <AdBanner where="solo" playing gameId={props.gameId} />}
      <div ref={stage} class="stage" />
      {touchUi ? (
        <TouchControls touch={input.touch} buttons={props.buttons} mode={props.touch} />
      ) : props.touch === "tap" && props.buttons.length === 0 ? null : (
        // A tap game without buttons shows its own keys (ADR 0027): "Move" would be wrong.
        <p class="hint">{keyboardHint(props.buttons)}</p>
      )}
      {status && <p role="status">{status}</p>}
      {!status && teach.teaching && <TeachCard teach={teach} touch={touchUi} />}
      {loadFailed && <TellUs />}
    </section>
  );
}

/** The chooser's banner: only once /api/me has said this person sees ads (ADR 0026). */
function SoloBanner() {
  useEffect(loadAds, []);
  return adsForMe.value === true ? <AdBanner where="solo" /> : null;
}
